import { Box, Grid, Link, styled } from '@mui/material';
import GeoDataOverviewChart from 'components/DueDiligenceProcess/components/GeoDataOverviewChart';
import useDueDiligenceProcesses from 'components/DueDiligenceProcess/hooks/useDueDiligenceProcesses';
import Filter from 'components/Filter/Filter';
import { Loader } from 'components/Forms';
import { ErrorState, FlexBox, PageSubTitle, SlideIndicator } from 'components/Structure';
import { useDialog, usePageTitle } from 'components/hooks';
import {
  CardIllustration,
  EmptyStateCard,
  InfoTooltip,
  ThemeButton,
  ThemeTypography,
} from 'designSystem';
import Icon from 'designSystem/Primitives/Icon/Icon';
import React, { FC } from 'react';
import { AvailableSizes, BackgroundTypes, IllustrationTypes } from 'types/enums';
import {
  DUE_DILIGENCE_PROCESSES,
  EDueDiligenceHistoryTableColumnKeys,
} from 'components/DueDiligenceProcess/constants/headerColumns';
import { StyledTable, StyledTableBody } from 'components/TableBase';
import DueDiligenceHistoryTableRow from 'components/DueDiligenceProcess/components/GeoDataHistoryTable/GeoDataHistoryTableRow';
import { useNavigate } from 'react-router-dom';
import useDueDiligenceProcessMutations from 'hooks/useDueDiligenceProcessMutations';
import { Link as RouterLink } from 'react-router-dom';

const LinkContainer = styled(Box)(({ theme }) => ({
  '& a': {
    textDecoration: 'none',
    color: theme.custom.themeColors.primary[100],
    fontSize: '11px !important',

    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

const EudrComplianceOverviewDueDiligence: FC = () => {
  usePageTitle('Compliance overview');
  const {
    dueDiligenceProcesses,
    geoDataProcessesSummary,
    filters,
    error,
    loading,
    totalCount,
    handleFilterChange,
    handleFilterReset,
  } = useDueDiligenceProcesses({
    processesPerPage: 3,
  });
  const { deleteDueDiligenceProcess } = useDueDiligenceProcessMutations();
  const { openDialog } = useDialog();
  const navigate = useNavigate();

  const handleNavigateToProcess = (id: string) => navigate(`/compliance/eudr/${id}/guide`);
  const handleCreateNewProcess = () => navigate('/compliance/eudr/guide?newProcess=1'); // Empty guide

  const handleDeleteDueDiligenceProcess = (id: string) => {
    openDialog({
      type: 'ALERT',
      props: {
        title: 'Delete due diligence process',
        text: 'Are you sure you want to delete this due diligence process? This action cannot be undone. However, the geo data will still be stored in your account.',
        cancelText: 'Cancel',
        submitText: 'Delete',
        onCancel: () => undefined,
        onSubmit: () => {
          deleteDueDiligenceProcess(id);
        },
      },
    });
  };

  const hasFiltersApplied = Object.values(filters).some(filter => filter.value !== undefined);

  if (error) {
    return <ErrorState />;
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {!dueDiligenceProcesses.length && !hasFiltersApplied && !loading && (
          <Box mt={2} mb={1}>
            <EmptyStateCard
              title="Start with your first due diligence process to become compliant with this regulation"
              onCreateItemClick={handleCreateNewProcess}
              buttonIcon={<Icon name="guide" />}
              buttonText="Start new process"
              size={AvailableSizes.LARGE}
              showSpark={false}
              renderForeground={
                <Box display="flex">
                  <SlideIndicator
                    onClick={() => undefined}
                    slideCount={6}
                    activeIndex={0}
                    stepColor="GRAY"
                    fillPrevious
                  />
                </Box>
              }
              renderIllustration={
                <CardIllustration
                  image={IllustrationTypes.COMPLIANCE}
                  itemType="LOCATIONS"
                  width={130}
                  right={50}
                  top={50}
                />
              }
              background={BackgroundTypes.MAP_LAYERS_RIGHT}
              data-testid="statements-getting-started-card"
              data-testid-button="statements-getting-started-card-button"
            />
          </Box>
        )}
        <Box mt={2} mb={2} width="100%">
          <Filter
            filters={filters}
            onFilterChange={handleFilterChange}
            onFilterReset={handleFilterReset}
          />
        </Box>
        {loading && (
          <Box position="relative" height="100%" p={5}>
            <Loader />
          </Box>
        )}

        {!loading && (
          <>
            <PageSubTitle title="Geo-data compliance status" spaceBetween={false}>
              <InfoTooltip
                size={AvailableSizes.SMALL}
                text="The geo-data compliance status shows the overall progress of the geo-data due diligence for all geo-data included into your compliance efforts. The status is updated automatically for every geo-data item."
              />
            </PageSubTitle>
            <Box mt={1} mb={2}>
              <GeoDataOverviewChart
                highRisks={geoDataProcessesSummary?.totalHighRiskPlots ?? 0}
                mediumRisks={geoDataProcessesSummary?.totalMediumRiskPlots ?? 0}
                lowRisks={geoDataProcessesSummary?.totalLowRiskPlots ?? 0}
                notAnalysed={geoDataProcessesSummary?.totalInProgressPlots ?? 0}
                inAnalysis={geoDataProcessesSummary?.totalInAnalysisPlots ?? 0}
                totalSize={geoDataProcessesSummary?.totalLandArea ?? 0}
                amountCommodities={geoDataProcessesSummary?.totalAmountOfCommodities ?? 0}
                amountPartners={geoDataProcessesSummary?.totalAmountOfSuppliers ?? 0}
                amountCountries={geoDataProcessesSummary?.totalAmountOfCountries ?? 0}
              />
            </Box>
            <FlexBox justifyContent="space-between" alignItems="center" width="100%">
              <PageSubTitle
                title="Geo-data processes"
                visibleItems={(totalCount ?? 0) > 3 ? 3 : undefined}
                totalItems={totalCount}
                spaceBetween={false}
              >
                <InfoTooltip
                  size={AvailableSizes.SMALL}
                  text="A geo-data process is created for every time the geo-data analysis guide is opened and geo-data is added. All geo-data processes are saved and listed in the due diligence history. Empty processes are not saved (no geo-data added)."
                />
              </PageSubTitle>
              <Box display="flex" alignItems="center" gap={2}>
                <LinkContainer>
                  <Link
                    component={RouterLink}
                    to="/compliance/eudr/due-diligence-history"
                    color="secondary"
                    className="small"
                  >
                    See all
                  </Link>
                </LinkContainer>
                <ThemeButton
                  onClick={handleCreateNewProcess}
                  size="small"
                  color="WHITE"
                  startIcon={<Icon size="small" name="guide" />}
                >
                  Start new process
                </ThemeButton>
              </Box>
            </FlexBox>
            <Box mt={1}>
              {!!dueDiligenceProcesses.length ? (
                <StyledTable aria-labelledby="tableTitle" aria-label="enhanced table">
                  <StyledTableBody>
                    {dueDiligenceProcesses.map(geoDataProcess => (
                      <DueDiligenceHistoryTableRow
                        key={geoDataProcess.id}
                        geoDataProcess={geoDataProcess}
                        headerColumns={DUE_DILIGENCE_PROCESSES.filter(
                          ({ id }) =>
                            id === EDueDiligenceHistoryTableColumnKeys.TITLE ||
                            id === EDueDiligenceHistoryTableColumnKeys.ACTIONS
                        )}
                        onNavigateToProcessGuide={handleNavigateToProcess}
                        onDeleteProcess={handleDeleteDueDiligenceProcess}
                      />
                    ))}
                  </StyledTableBody>
                </StyledTable>
              ) : (
                <ThemeTypography variant="BODY_MEDIUM" color="GRAY_60">
                  No processes started yet.
                </ThemeTypography>
              )}
            </Box>
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default EudrComplianceOverviewDueDiligence;
